// Copyright 2017 Sorama B.V.

import {
    autoinject,
    bindable,
    BindingEngine,
    bindingMode,
    computedFrom,
    containerless,
    customElement,
    Disposable,
    observable,
    TaskQueue,
} from "aurelia-framework";
import { AudioStreamsPlayer } from "controls/audio-streams-player/AudioStreamsPlayer";
import { IAnimator } from "libraries/Export/IAnimator";
import { IPlayablePropertiesService, PlayableProperty } from "services/PlayableProperties/IPlayablePropertiesService";
import { Generic } from "units/Generic";
import { Time } from "units/Time";
import { Unit, UnitValue } from "units/Unit";

export interface FramePlayer {
    readonly currentKey: UnitValue;
}

export interface FrameGenerator<T> {
    readonly keyUnit: Unit;
    readonly minKey: UnitValue;
    readonly maxKey: UnitValue;

    /**
     * Initializes the frame context type T.
     */
    initialize(player: FramePlayer): T;

    /**
     * Generates framedata for the specified key.
     * @param key Key to generate framedata for.
     * @param frameContext Framecontext that needs to receive the data.
     */
    generate(key: UnitValue, frameContext: T): void;
}

@customElement("griddataplayer")
@containerless
@autoinject
export class GridDataPlayer implements IAnimator, FramePlayer {
    private static InstanceCount = 0;
    private readonly LocalPlayablePropertiesBaseId = "griddataplayer";

    public selectableRates: number[] = [1, 0.5, 0.1, 0.05];

    //#region Injected Services
    private readonly playablePropertiesService: IPlayablePropertiesService;
    private readonly bindingEngine: BindingEngine;
    private readonly taskQueue: TaskQueue;

    private audioStreamsPlayer: AudioStreamsPlayer;
    //#endregion

    // #region Bindables
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public frameContext: unknown;

    @bindable
    public propertiesHaveChanged: boolean;

    @bindable
    public frameGenerator: FrameGenerator<unknown>;

    @bindable
    public playbackRate = 1;

    @bindable({ defaultValue: new UnitValue(1, Time.second) })
    public baseSpeed: UnitValue;

    @bindable
    public playableId: string;

    @bindable
    public fullScreenElement: Element;

    @bindable
    public measurementHasChanged: boolean;

    @bindable
    public soundTracksVisible: boolean;

    @bindable
    public speedVisible: boolean;

    @bindable
    public autoPlay = false;
    //#endregion

    //#region Fields
    private _localPlayableId: string;
    private currentKeyObservers: Disposable[] = [];
    private isAnimating: boolean;

    private isFullScreen: boolean;

    @observable
    private audioSelected: boolean;

    @bindable
    public selectedTrack: string;

    //#endregion

    //#region View Fields
    @computedFrom("currentKey")
    public get currentFrameTime(): UnitValue {
        return this.currentKey;
    }

    @computedFrom("currentKey")
    public get playKey(): number {
        return this.currentKey && this.frameGenerator ? this.currentKey.toUnit(this.frameGenerator.keyUnit) : 0;
    }

    public set playKey(value: number) {
        if (!this.frameGenerator) return;

        const prop = this.playablePropertiesService.playableProperties[this._localPlayableId];

        if (prop) {
            prop.currentKey = new UnitValue(value, this.frameGenerator.keyUnit);
        }
    }

    @computedFrom("frameGenerator")
    public get hasFrames(): boolean {
        return !!this.frameGenerator;
    }

    @observable
    public currentKey: UnitValue = new UnitValue(0, Generic.unitless);
    public isPlaying = false;

    //#endregion

    //#region Constructor and Initialisation

    constructor(
        playablePropertiesService: IPlayablePropertiesService,
        bindingEngine: BindingEngine,
        taskQueue: TaskQueue
    ) {
        this.playablePropertiesService = playablePropertiesService;
        this.bindingEngine = bindingEngine;
        this.taskQueue = taskQueue;
    }

    public bind(): void {
        document.addEventListener("fullscreenchange", this.fullscreenChanged);
        this.frameGeneratorChanged();
    }

    public unbind(): void {
        if (!this.playableId && this._localPlayableId) {
            this.playablePropertiesService.unregister(this._localPlayableId);
        }

        while (this.currentKeyObservers.length > 0) this.currentKeyObservers.pop().dispose();

        document.removeEventListener("fullscreenchange", this.fullscreenChanged);
    }

    //#endregion

    //#region Change Handlers

    public frameGeneratorChanged(): void {
        this.updateFrameGenerator();
    }

    public playableIdChanged(newValue: string, oldValue: string): void {
        if (!oldValue && !this._localPlayableId) {
            // There was no global defined unit, so unregister the local instance
            this.playablePropertiesService.unregister(this._localPlayableId);
        }

        // Clear any observers we have
        while (this.currentKeyObservers.length > 0) this.currentKeyObservers.pop().dispose();

        if (!this.frameGenerator) {
            // We've not been initialized yet
            return;
        }

        if (this.playableId) {
            // Check if the global playable already exists
            if (!this.playablePropertiesService.playableProperties[this.playableId]) {
                this.playablePropertiesService.register(
                    this.playableId,
                    new PlayableProperty(this.frameGenerator.minKey, this.frameGenerator.maxKey, this.baseSpeed)
                );
            }

            this._localPlayableId = this.playableId;
        } else {
            // Create a local instance
            this._localPlayableId = this.LocalPlayablePropertiesBaseId + GridDataPlayer.InstanceCount++;
            this.playablePropertiesService.register(
                this._localPlayableId,
                new PlayableProperty(this.frameGenerator.minKey, this.frameGenerator.maxKey, this.baseSpeed)
            );
        }

        const playableProps = this.playablePropertiesService.playableProperties[this._localPlayableId];

        this.currentKeyObservers = [
            this.bindingEngine.propertyObserver(playableProps, "currentKey").subscribe(() => {
                if (this.isAnimating) return; // Do not listen to the main clock when animating

                this.currentKey = this.playablePropertiesService.playableProperties[this._localPlayableId].currentKey;
            }),
            this.bindingEngine.propertyObserver(playableProps, "isPlaying").subscribe(() => {
                if (this.isAnimating) return; // Do not listen to the main clock when animating

                this.isPlaying = this.playablePropertiesService.playableProperties[this._localPlayableId].isPlaying;
            }),
        ];

        this.currentKey = this.playablePropertiesService.playableProperties[this._localPlayableId].currentKey;
        this.isPlaying = this.playablePropertiesService.playableProperties[this._localPlayableId].isPlaying;
    }

    public measurementHasChangedChanged(): void {
        this.audioSelected = false;
    }

    public currentKeyChanged(newValue: UnitValue, oldValue: UnitValue): void {
        if (!oldValue || this.currentKey.value == undefined) {
            return;
        }

        if (newValue.value < oldValue.value) {
            //Match audioElement currentTime when loop is at start again
            if (this.audioStreamsPlayer && this.audioStreamsPlayer.audioElement && this.audioSelected) {
                this.audioStreamsPlayer.audioElement.currentTime = newValue.value;
            }
        }

        this.frameGenerator.generate(this.currentKey, this.frameContext);
    }

    private fullscreenChanged = () => {
        this.isFullScreen = !!document.fullscreenElement;
    };

    //#endregion

    //#region Update functions

    private updateFrameGenerator(): void {
        const playing = this.isPlaying;

        if (!this.frameGenerator) {
            this.frameContext = undefined;
        } else {
            this.frameContext = this.frameGenerator.initialize(this);
            this.currentKey = this.frameGenerator.minKey;
            this.frameGenerator.generate(this.currentKey, this.frameContext);
        }

        this.playableIdChanged(this.playableId, this.playableId);

        if (playing != this.isPlaying || (this.autoPlay && !playing)) {
            this.togglePlaying();
        }
    }

    //#endregion

    //#region Input Methods

    public togglePlaying(): void {
        if (!this.frameGenerator || !this._localPlayableId) return;

        const prop = this.playablePropertiesService.playableProperties[this._localPlayableId];

        if (prop.isPlaying) {
            this.playablePropertiesService.pause(this._localPlayableId);

            //Check for active audioStreams
            if (this.audioStreamsPlayer && this.audioStreamsPlayer.audioElement && this.audioSelected) {
                this.audioStreamsPlayer.stopPlaying();
            }
        } else {
            prop.maxKey = new UnitValue(this.frameGenerator.maxKey.value, this.frameGenerator.maxKey.unit);
            prop.minKey = new UnitValue(this.frameGenerator.minKey.value, this.frameGenerator.minKey.unit);

            this.playablePropertiesService.play(this._localPlayableId);

            //Check for active audioStreams
            if (this.audioStreamsPlayer && this.audioStreamsPlayer.audioElement && this.audioSelected) {
                this.audioStreamsPlayer.startPlaying();
            }
        }
    }

    public toggleFullScreen(): void {
        if (this.isFullScreen) {
            document.exitFullscreen();
        } else if (this.fullScreenElement) {
            this.fullScreenElement.requestFullscreen();
        }
    }

    public selectAudioStream(selectedTrack?: string): void {
        if (selectedTrack) {
            this.selectedTrack = selectedTrack;
        } else {
            this.selectedTrack = undefined;
            this.audioSelected = false;
        }

        this.soundTracksVisible = false;
    }

    public selectPlaybackRate(selectedSpeed: number): void {
        if (this._localPlayableId) {
            this.playablePropertiesService.playableProperties[this._localPlayableId].playbackRate = selectedSpeed;
            this.playbackRate = selectedSpeed;

            if (selectedSpeed != 1) {
                this.selectAudioStream();
            }

            this.speedVisible = false;
        }
    }

    public resetPlayback(): void {
        // We can skip reset if we do not actually have any frameGenerator loaded yet
        if (this.frameGenerator) {
            // Reset audio and playback settings
            this.selectAudioStream();
            this.selectPlaybackRate(1);

            // Pause and reset to start of video
            if (this.isPlaying) {
                this.togglePlaying();
            }

            this.playKey = this.frameGenerator.minKey.value;
        }
    }

    //#endregion

    //#region IAnimatable

    public startAnimation(): Promise<void> {
        if (!this.frameGenerator) return Promise.reject("No framedata available");

        this.isAnimating = true;
        this.playablePropertiesService.pause(this._localPlayableId);
        this.currentKey = this.frameGenerator.minKey;

        // Wait for the taskqueue to be flushed
        return new Promise((resolve) => {
            this.taskQueue.queueTask(() => resolve());
        });
    }

    public nextAnimationStep(): Promise<boolean> {
        if (!this.frameGenerator) return Promise.reject("No framedata available");

        const stepSize =
            this.playablePropertiesService.playableProperties[this._localPlayableId].playbackRate /
            this.playablePropertiesService.playableProperties[this._localPlayableId].updateInterval;

        const keyStep = new UnitValue(stepSize * this.baseSpeed.value, this.baseSpeed.unit).toUnit(
            this.frameGenerator.keyUnit
        );

        this.currentKey = new UnitValue(this.currentKey.value + keyStep, this.frameGenerator.keyUnit);

        const lastFrame = !(this.currentKey.value >= this.frameGenerator.maxKey.value);
        return new Promise((resolve) => {
            this.taskQueue.queueTask(() => {
                if (lastFrame) {
                    this.isAnimating = false;
                }

                resolve(lastFrame);
            });
        });
    }
    //#endregion
}
